import { initI18n, t, translatePage, setLocale } from "./i18n.js";
import { eventBus } from "./eventbus.js";
import { api } from "./api.js";
import { renderLoginPage } from "./pages/login-page.js";
import { renderTripsPage } from "./pages/trips-page.js";
import { renderTripDetailPage } from "./pages/trip-detail-page.js";
import { renderTripEditorPage } from "./pages/trip-editor-page.js";
import { renderLocationEditorPage } from "./pages/location-editor-page.js";
import { renderLocationViewPage } from "./pages/location-view-page.js";
import { renderSettingsPage } from "./pages/settings-page.js";
import { renderAdminPage } from "./pages/admin-page.js";
import { renderNotFoundPage } from "./pages/not-found-page.js";
import { renderSuggestPage } from "./pages/suggest-page.js";
import { renderLoading } from "./components/loading.js";
import { renderUserMenu } from "./components/user-menu.js";
import { createRouter, navigate } from "./router.js";
import { initTheme, setTheme } from "./theme.js";
import { initMapTheme, setMapTheme, rememberPosition } from "./map-theme.js";
import { TRIP_TABS } from "./trip-tabs.js";
import { setCurrentUser } from "./session.js";

const app = document.getElementById("app");

// Trip detail tabs (Locations/Map/Itinerary/Files/Checklists/Settings)
// are each a real 3-segment route so the URL reflects the active tab and
// back/forward works - see trip-detail-page.js. They're all distinct
// literals at the same depth, so there's no ordering conflict between
// them; "/trips/:tripId" (no tab segment) is handled last and
// canonicalizes itself to "/trips/:tripId/locations" on render.
const tripTabRoutes = TRIP_TABS.map(({ key }) => ({
  pattern: `/trips/:tripId/${key}`,
  render: (container, params, signal) => renderTripDetailPage(container, { ...params, tab: key }, signal),
}));

const routes = [
  // index.html is served at "/", so that's where a fresh visit lands.
  // Canonicalize it to the trips list rather than letting it fall through to
  // the catch-all below and report the app's own entry point as not found.
  { pattern: "/", render: () => navigate("/trips") },
  { pattern: "/trips", render: renderTripsPage },
  // Account settings (not a trip's Settings tab - see pages/settings-page.js).
  { pattern: "/settings", render: renderSettingsPage },
  // Account *administration*, for admins only. The page checks that itself and
  // renders not-found otherwise, since the URL is typeable.
  { pattern: "/admin", render: renderAdminPage },
  // "/trips/new" must precede "/trips/:tripId", and
  // "/trips/:tripId/locations/new" must precede
  // "/trips/:tripId/locations/:locationId" - same segment counts, and the
  // router's match() takes the first pattern that fits, so the literal route
  // would otherwise never be reached (":param" swallows "new"/"edit").
  { pattern: "/trips/new", render: renderTripEditorPage },
  // Its own route rather than a panel on the locations tab: see suggest-page.js.
  { pattern: "/trips/:tripId/suggest", render: renderSuggestPage },
  { pattern: "/trips/:tripId/locations/new", render: renderLocationEditorPage },
  { pattern: "/trips/:tripId/locations/:locationId/edit", render: renderLocationEditorPage },
  { pattern: "/trips/:tripId/locations/:locationId", render: renderLocationViewPage },
  ...tripTabRoutes,
  { pattern: "/trips/:tripId", render: renderTripDetailPage },
  // The catch-all. Must be last: the router takes the first pattern that
  // fits, and "*" fits everything.
  { pattern: "*", render: renderNotFoundPage },
];

// The listener that re-renders the app when the language changes, kept at module
// scope so a re-mount (logging out and back in) replaces it instead of stacking
// a second copy that renders into a detached header.
let onLocaleChanged = null;
// The router, for the same reason: a re-mount destroys the old one first, so
// its listeners and the page it was showing are gone before the new one starts.
let router = null;

async function renderAuthenticated(user) {
  app.innerHTML = `
    <header class="app-header">
      <!-- The lockup, and a link home: the mark is decorative (the wordmark
           beside it already names the app, so a second accessible name would
           be read twice), and data-link lets the router handle the click
           instead of the browser reloading the whole app. -->
      <a class="app-brand" href="/trips" data-link>
        <span class="brand-mark" aria-hidden="true"></span>
        <span class="app-brand__wordmark">${t("app.name")}</span>
      </a>
      <div class="user-menu-slot"></div>
    </header>
    <main id="main"></main>
  `;
  translatePage(app);

  async function onLogout() {
    await api.post("/auth/logout");
    // Before boot() replaces the page with the login form: the page that was
    // open is gone too, and anything it still had running should hear so.
    router.destroy();
    boot();
  }

  renderUserMenu(app.querySelector(".user-menu-slot"), user, { onLogout });

  router?.destroy();
  router = createRouter(routes, document.getElementById("main"));

  // i18n.js's setLocale re-runs translatePage, which only rewrites declarative
  // data-i18n attributes - every string built through t() in JS (menu labels,
  // category filters, day headings) would keep the old language until the next
  // navigation. So a locale change re-renders the header and the current route.
  if (onLocaleChanged) eventBus.removeEventListener("locale-changed", onLocaleChanged);
  onLocaleChanged = () => {
    renderUserMenu(app.querySelector(".user-menu-slot"), user, { onLogout });
    router.render();
  };
  eventBus.addEventListener("locale-changed", onLocaleChanged);

  router.render();
}

async function boot(mePromise) {
  // Replaces index.html's static copy of the same loader with the translated
  // one, so the wait runs unbroken from first paint to the first route - the
  // ring does not restart, because the markup it swaps in is identical.
  renderLoading(app, { size: "lg" });
  try {
    const user = await mePromise;
    setCurrentUser(user);
    await renderAuthenticated(user);
  } catch {
    const user = await renderLoginPage(app);
    setCurrentUser(user);
    await renderAuthenticated(user);
  }
}

// The theme is already on <html> from index.html's inline script; this hooks up
// the part that needs a live listener - following the OS while the preference is
// "auto" and the tab stays open.
initTheme();
// And the map's own light/dark, which is a separate preference with a fourth
// state ("follow the app") -- see map-theme.js. Hooked up here because its
// day/night mode has a timer to arm and a position to look for, neither of
// which should wait for a map to be opened.
initMapTheme();

// The running app's own setters, for the UI suite. The specs used to import
// /js/theme.js and friends into the page, which worked while the page ran
// those very modules; against the bundle (Stage 45) such an import is a second
// copy with its own event bus and its own messages, so a theme change made
// through it never reaches the app. These are the same settings a reader
// changes from the settings page, so naming them here gives nothing away.
window.caravel = Object.freeze({ setTheme, setMapTheme, rememberPosition, setLocale });

// Asked for alongside the locale rather than after it: neither needs the
// other, and on a warm load the locale is a cache hit while this is a round
// trip. The no-op catch only keeps a logged-out load (a 401 here) from being
// reported as unhandled before boot() gets to it; boot() still sees the error.
const mePromise = api.get("/auth/me");
mePromise.catch(() => {});
initI18n().then(() => boot(mePromise));

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      // Non-fatal: the app works fully online without the service worker.
    });
  });
}
